/**
 * Working-day arithmetic helper (Europe/Warsaw calendar).
 *
 * The single source of truth for "add N working days" used across every
 * reservation TTL computation, regardless of which path creates the
 * reservation: `apps/api/src/reservations/ttl-evaluator.ts` (direct /
 * manual creation) and `packages/db/.../promote-next-queue-entry.ts`
 * (waitlist auto-promotion) both call this function so the two paths always
 * agree on the same working-day boundary.
 *
 * Weekend detection uses the Europe/Warsaw calendar date (not UTC) so a
 * timestamp near midnight UTC — which can already be the next (or still the
 * previous) calendar day in Warsaw — resolves to the working day a human in
 * Poland would expect. Fixes CU-86cb4wjx1, where the `@ooh/db` promotion
 * path used to compute weekends from the raw UTC day-of-week and could
 * disagree with the Warsaw-correct `apps/api` TTL evaluator near midnight
 * or over a weekend. See `openspec/specs/reservation-waitlist/spec.md`
 * "FCFS promotion query with re-validation" for the current spec text this
 * helper satisfies.
 */

export const DAY_MS = 24 * 60 * 60 * 1000;
export const WARSAW_TIME_ZONE = 'Europe/Warsaw';

const warsawDateFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: WARSAW_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour12: false,
});

const warsawDateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: WARSAW_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/**
 * Add `days` calendar days to `date`. Shared with `apps/api/src/reservations/
 * ttl-evaluator.ts`, which still needs plain calendar-day arithmetic for its
 * 7-calendar-day and Friday-noon branches.
 */
export function addCalendarDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * DAY_MS);
}

/**
 * Returns the day-of-week (0=Sun .. 6=Sat) of `date`'s Europe/Warsaw
 * calendar date, evaluated as a UTC weekday to sidestep local-system-TZ
 * dependency in the intermediate `Date`.
 */
function warsawWeekday(date: Date): number {
  const parts = warsawDateFormatter.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');

  return new Date(Date.UTC(pick('year'), pick('month') - 1, pick('day'))).getUTCDay();
}

/**
 * Returns a new Date that is `days` working days after `from`. Working days
 * exclude Saturday and Sunday on the Europe/Warsaw calendar.
 */
export function addWorkingDays(from: Date, days: number): Date {
  let result = new Date(from.getTime());
  let remaining = days;

  while (remaining > 0) {
    result = addCalendarDays(result, 1);
    const weekday = warsawWeekday(result);
    if (weekday !== 0 && weekday !== 6) {
      remaining -= 1;
    }
  }

  return result;
}

export interface WarsawParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/**
 * Returns `date`'s Europe/Warsaw calendar year and month (1-12). Used by
 * offer-draft-numbering to resolve the campaign number's month/year elements
 * and the per-client ordinal's monthly reset boundary in the sales team's
 * timezone rather than UTC, so a draft created late on the last evening of a
 * month is filed under that month (offer-draft-numbering / "Draft created
 * just before midnight at a month boundary").
 */
export function getWarsawYearMonth(date: Date): { year: number; month: number } {
  const parts = warsawDateFormatter.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');

  return { year: pick('year'), month: pick('month') };
}

/**
 * Converts Warsaw-local Y/M/D/H/M/S into the equivalent UTC instant,
 * correcting for DST via a round-trip through the formatter until the offset
 * stabilizes. Single shared implementation for every DST-correct local-to-UTC
 * conversion in the reservation TTL/signing-window/queue-scheduling paths —
 * `apps/api/src/reservations/ttl-evaluator.ts`'s `localPartsToUtc` calls this
 * directly rather than keeping its own copy, so a future DST-edge-case fix
 * can't apply to one call site and silently miss the other.
 */
export function warsawPartsToUtc(parts: WarsawParts): Date {
  let candidate = new Date(
    Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second),
  );

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const formatted = warsawDateTimeFormatter.formatToParts(candidate);
    const pick = (type: Intl.DateTimeFormatPartTypes) =>
      Number(formatted.find((part) => part.type === type)?.value ?? '0');
    const diffMs =
      Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) -
      Date.UTC(
        pick('year'),
        pick('month') - 1,
        pick('day'),
        pick('hour'),
        pick('minute'),
        pick('second'),
      );

    if (diffMs === 0) return candidate;
    candidate = new Date(candidate.getTime() + diffMs);
  }

  return candidate;
}

/**
 * Converts Warsaw-local Y/M/D 00:00:00 into the equivalent UTC instant.
 * Midnight itself is never ambiguous or skipped under Poland's DST rules
 * (transitions land at 02:00/03:00 local).
 */
function warsawMidnightUtc(year: number, month: number, day: number): Date {
  return warsawPartsToUtc({ year, month, day, hour: 0, minute: 0, second: 0 });
}

/**
 * Returns midnight (00:00) Europe/Warsaw time of the next working day
 * strictly after `from`'s Warsaw calendar date, skipping Saturday/Sunday.
 *
 * Used to schedule queue promotion for "the next working day" rather than
 * promoting the moment a reservation is released — see the
 * `reservation-waitlist` capability's "Scheduled next-working-day promotion".
 */
export function nextWorkingDayStart(from: Date): Date {
  let probe = addCalendarDays(from, 1);
  while (warsawWeekday(probe) === 0 || warsawWeekday(probe) === 6) {
    probe = addCalendarDays(probe, 1);
  }

  const parts = warsawDateFormatter.formatToParts(probe);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');

  return warsawMidnightUtc(pick('year'), pick('month'), pick('day'));
}

// ─── manual-reservation-expiry-editing: DST-aware date-only conversion ──────
//
// Poland's Europe/Warsaw calendar only ever has two possible UTC offsets:
// CET (+01:00, winter) and CEST (+02:00, summer). This lets the ambiguous
// (autumn fall-back) and nonexistent (spring-forward) cases be resolved by
// checking both fixed candidate offsets directly, rather than needing a
// general-purpose timezone-offset-enumeration algorithm.

const CET_OFFSET_MINUTES = 60;
const CEST_OFFSET_MINUTES = 120;

/** A Warsaw calendar date, independent of any wall-clock time. */
export interface WarsawDateOnly {
  year: number;
  month: number;
  day: number;
}

/** A Warsaw wall-clock time of day, independent of any calendar date. */
export interface WarsawWallTime {
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
}

export type WarsawConversionResult =
  | { ok: true; instant: Date }
  | { ok: false; reason: 'NONEXISTENT' };

/**
 * Extracts `date`'s Warsaw-local wall-clock time of day. Milliseconds are
 * timezone-invariant (Poland's DST offsets are whole minutes), so they are
 * read directly from the UTC instant rather than through the Intl formatter.
 */
export function getWarsawWallTime(date: Date): WarsawWallTime {
  const parts = warsawDateTimeFormatter.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');

  return {
    hour: pick('hour'),
    minute: pick('minute'),
    second: pick('second'),
    millisecond: date.getUTCMilliseconds(),
  };
}

/** Returns `date`'s Europe/Warsaw UTC offset in minutes (60 for CET, 120 for CEST). */
function warsawUtcOffsetMinutes(date: Date): number {
  const parts = warsawDateTimeFormatter.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  const localAsUtcMs = Date.UTC(
    pick('year'),
    pick('month') - 1,
    pick('day'),
    pick('hour'),
    pick('minute'),
    pick('second'),
  );

  return Math.round((localAsUtcMs - date.getTime()) / 60000);
}

/** Builds the UTC instant assuming `wallTime` on `date` carries the given fixed offset. */
function candidateAtOffset(
  date: WarsawDateOnly,
  wallTime: WarsawWallTime,
  offsetMinutes: number,
): Date {
  return new Date(
    Date.UTC(
      date.year,
      date.month - 1,
      date.day,
      wallTime.hour,
      wallTime.minute,
      wallTime.second,
      wallTime.millisecond,
    ) -
      offsetMinutes * 60000,
  );
}

/** True when `candidate` actually formats back to `date`/`wallTime` in Europe/Warsaw. */
function candidateRoundTrips(
  candidate: Date,
  date: WarsawDateOnly,
  wallTime: WarsawWallTime,
): boolean {
  const parts = warsawDateTimeFormatter.formatToParts(candidate);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');

  return (
    pick('year') === date.year &&
    pick('month') === date.month &&
    pick('day') === date.day &&
    pick('hour') === wallTime.hour &&
    pick('minute') === wallTime.minute &&
    pick('second') === wallTime.second
  );
}

/**
 * Combines a Warsaw calendar date with a preserved wall-clock time of day,
 * resolving the two DST edge cases deterministically:
 *
 * - Nonexistent (spring-forward gap): neither CET nor CEST round-trips ->
 *   `{ ok: false, reason: 'NONEXISTENT' }`.
 * - Ambiguous (autumn fall-back): both CET and CEST round-trip -> preserve
 *   `preferredOffsetMinutes` (the original effective deadline's UTC offset)
 *   when it is CET; otherwise (CEST, or any other/unexpected value)
 *   deterministically choose the earlier (CEST, pre-transition) occurrence.
 */
export function combineWarsawDateWithWallTime(
  date: WarsawDateOnly,
  wallTime: WarsawWallTime,
  preferredOffsetMinutes: number,
): WarsawConversionResult {
  const cetCandidate = candidateAtOffset(date, wallTime, CET_OFFSET_MINUTES);
  const cestCandidate = candidateAtOffset(date, wallTime, CEST_OFFSET_MINUTES);
  const cetValid = candidateRoundTrips(cetCandidate, date, wallTime);
  const cestValid = candidateRoundTrips(cestCandidate, date, wallTime);

  if (cetValid && cestValid) {
    return {
      ok: true,
      instant: preferredOffsetMinutes === CET_OFFSET_MINUTES ? cetCandidate : cestCandidate,
    };
  }
  if (cetValid) return { ok: true, instant: cetCandidate };
  if (cestValid) return { ok: true, instant: cestCandidate };
  return { ok: false, reason: 'NONEXISTENT' };
}

/**
 * Preserves `currentInstant`'s Warsaw-local wall-clock time of day (and UTC
 * offset, for ambiguous-hour disambiguation) onto `selectedDate`. This is the
 * primary entry point used to derive a reservation's new effective expiry
 * from its selected calendar date — see `manual-reservation-expiry-editing`'s
 * "Warsaw date-only conversion preserves the effective time of day".
 */
export function preserveWarsawTimeOfDayOnDate(
  currentInstant: Date,
  selectedDate: WarsawDateOnly,
): WarsawConversionResult {
  const wallTime = getWarsawWallTime(currentInstant);
  const preferredOffsetMinutes = warsawUtcOffsetMinutes(currentInstant);
  return combineWarsawDateWithWallTime(selectedDate, wallTime, preferredOffsetMinutes);
}

// ─── reservation-mixed-range-and-target-expiry: shared "do kiedy" deadline ──
//
// A calendar date named for a bulk reservation/queue-join means end of that
// day, Europe/Warsaw — a picker's local midnight would otherwise expire a
// hold quoted as "do 18.08" at the START of the 18th. The wire value stays a
// single field name (ttlExpiresAt) on every entry point; whether it is a
// bare calendar date or an explicit instant is decided by wire FORMAT
// (YYYY-MM-DD vs. a full ISO datetime), never by inferring intent from the
// parsed value (proposal.md's Decisions explicitly rejects that). Every
// reservation-creation path that accepts this value (batch-reserve,
// lock-reservations, queue-join) MUST resolve it through this one function.

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Returns the Europe/Warsaw end-of-day instant (23:59:59.999 local) for `date`. */
export function endOfWarsawDay(date: WarsawDateOnly): Date {
  const secondBoundary = warsawPartsToUtc({ ...date, hour: 23, minute: 59, second: 59 });
  return new Date(secondBoundary.getTime() + 999);
}

/**
 * Resolves a wire-supplied `ttlExpiresAt` value to the instant it means: a
 * bare calendar date (`YYYY-MM-DD`) normalises to end of day Europe/Warsaw;
 * any other (full ISO instant) form passes through unchanged.
 */
export function resolveTtlExpiresAt(raw: string): Date {
  if (DATE_ONLY_PATTERN.test(raw)) {
    const [year, month, day] = raw.split('-').map(Number);
    return endOfWarsawDay({ year: year!, month: month!, day: day! });
  }
  return new Date(raw);
}

/** Extracts `date`'s Warsaw-local calendar date (year/month/day), independent of time of day. */
function warsawDateOnlyOf(date: Date): WarsawDateOnly {
  const parts = warsawDateFormatter.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  return { year: pick('year'), month: pick('month'), day: pick('day') };
}

export type TtlExpiresAtBoundResult =
  | { ok: true; instant: Date }
  | { ok: false; instant: Date; reason: 'PAST' | 'BEYOND_HORIZON' };

/**
 * Resolves a wire-supplied `ttlExpiresAt` value (via `resolveTtlExpiresAt`)
 * and reports whether it is usable: not in the past relative to `now`, and
 * not more than `horizonDays` ahead of `now`'s Warsaw calendar date. The
 * horizon is evaluated as a Warsaw CALENDAR date boundary (end of day on
 * `now`'s Warsaw date + `horizonDays`), matching how a caller picks a date on
 * a calendar UI — not a raw `horizonDays * 24h` millisecond offset from the
 * exact `now` instant.
 *
 * Callers pass `TTL_HORIZON_DAYS` from `../schemas/reservation-batch.js` as
 * `horizonDays` — this function stays free of any dependency on that schema
 * module so `packages/contracts/src/utils` never imports `src/schemas`.
 * `apps/api`'s HTTP-level rejection (batch-reserve, lock-reservations, and
 * the queue-join endpoint) is the caller of this function; it is not wired
 * up here.
 */
export function evaluateTtlExpiresAtBound(
  raw: string,
  now: Date,
  horizonDays: number,
): TtlExpiresAtBoundResult {
  const instant = resolveTtlExpiresAt(raw);
  if (instant.getTime() <= now.getTime()) {
    return { ok: false, instant, reason: 'PAST' };
  }

  const horizonInstant = endOfWarsawDay(warsawDateOnlyOf(addCalendarDays(now, horizonDays)));
  if (instant.getTime() > horizonInstant.getTime()) {
    return { ok: false, instant, reason: 'BEYOND_HORIZON' };
  }

  return { ok: true, instant };
}
